/**
 * On-demand KPI management filters. Same chrome as other catalogs:
 * funnel beside Search, panel under the toolbar, badge + Clear when applied.
 * Status is Active / Inactive. Deleted rows are removed, not filtered.
 * Function is not a definition field — grouping is packs and tags.
 */

import React from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
  AE_LABEL_COMPACT,
  AE_SELECT_COMPACT
} from '@/components/assessment-engine/layout/aeTheme';
import { useLanguage } from '@/state/language/LanguageProvider';

export type KpiCatalogFilterValues = {
  origin: '' | 'tenant' | 'platform';
  status: '' | 'ACTIVE' | 'INACTIVE';
  tag: string;
};

export const EMPTY_KPI_CATALOG_FILTERS: KpiCatalogFilterValues = {
  origin: '',
  status: '',
  tag: ''
};

export function kpiFiltersToQueryParams(
  filters: KpiCatalogFilterValues
): Record<string, string | undefined> {
  return {
    origin: filters.origin || undefined,
    status: filters.status || undefined,
    tag: filters.tag.trim() || undefined
  };
}

function countActiveFilters(filters: KpiCatalogFilterValues): number {
  return [filters.origin, filters.status, filters.tag.trim()].filter((value) => value !== '').length;
}

export function kpiCatalogFiltersActive(filters: KpiCatalogFilterValues): boolean {
  return countActiveFilters(filters) > 0;
}

type ToggleProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: KpiCatalogFilterValues;
  onChange: (next: KpiCatalogFilterValues) => void;
};

export const KpiCatalogFilterToggle: React.FC<ToggleProps> = ({
  open,
  onOpenChange,
  value,
  onChange
}) => {
  const { t } = useLanguage();
  const activeCount = countActiveFilters(value);
  const active = activeCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="kpi-catalog-filters-panel"
        onClick={() => onOpenChange(!open)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] ${
          open || active
            ? 'border-[var(--color-accent-bg)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
            : 'border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]'
        }`}
      >
        <FunnelIcon className="h-4 w-4" aria-hidden />
        {t('kpi.filtersHeading') || 'Filters'}
        {active && (
          <span className="rounded-full bg-[var(--color-surface-bg)] px-1.5 text-xs font-semibold text-[var(--color-surface-text)]">
            {activeCount}
          </span>
        )}
      </button>
      {active && (
        <button
          type="button"
          onClick={() => onChange({ ...EMPTY_KPI_CATALOG_FILTERS })}
          className="text-xs font-medium text-[var(--color-accent-bg)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('kpi.filtersClear') || 'Clear filters'}
        </button>
      )}
    </div>
  );
};

type PanelProps = {
  value: KpiCatalogFilterValues;
  onChange: (next: KpiCatalogFilterValues) => void;
};

export const KpiCatalogFilterPanel: React.FC<PanelProps> = ({ value, onChange }) => {
  const { t } = useLanguage();

  const patch = (partial: Partial<KpiCatalogFilterValues>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <div
      id="kpi-catalog-filters-panel"
      className="border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 pb-2 pt-2"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="kpi-filter-origin">
            {t('kpi.filterOrigin') || 'Origin'}
          </label>
          <select
            id="kpi-filter-origin"
            className={AE_SELECT_COMPACT}
            value={value.origin}
            onChange={(event) =>
              patch({ origin: event.target.value as KpiCatalogFilterValues['origin'] })
            }
          >
            <option value="">{t('kpi.filterAll') || 'All'}</option>
            <option value="tenant">{t('kpi.filterOriginTenant') || 'Tenant copies'}</option>
            <option value="platform">{t('kpi.filterOriginPlatform') || 'Platform masters'}</option>
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="kpi-filter-status">
            {t('kpi.filterStatus') || 'Status'}
          </label>
          <select
            id="kpi-filter-status"
            className={AE_SELECT_COMPACT}
            value={value.status}
            onChange={(event) =>
              patch({ status: event.target.value as KpiCatalogFilterValues['status'] })
            }
          >
            <option value="">{t('kpi.filterAll') || 'All'}</option>
            <option value="ACTIVE">{t('kpi.filterStatusActive') || 'Active'}</option>
            <option value="INACTIVE">{t('kpi.filterStatusInactive') || 'Inactive'}</option>
          </select>
        </div>

        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="kpi-filter-tag">
            {t('kpi.filterTag') || 'Tag'}
          </label>
          <input
            id="kpi-filter-tag"
            type="text"
            className={AE_SELECT_COMPACT}
            value={value.tag}
            placeholder={t('kpi.tagsHint') || 'Comma-separated labels. Matching ignores case.'}
            onChange={(event) => patch({ tag: event.target.value })}
          />
        </div>
      </div>
    </div>
  );
};
